import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { dashboardApi } from "../../api/dashboardApi";
import { useAuth } from "../../auth/AuthContext";

import type { MachineDashboardRow } from "../../types/machinesDashboard";
import { MachineKpiCards } from "../../components/Machines/MachineKpiCards";
import { MachinesTable } from "../../components/Machines/MachinesTable";
import { IrinoxConsumptionChart } from "../../components/Machines/IrinoxConsumptionChart";
import { IrinoxEnergyOverview } from "../../components/Machines/IrinoxEnergyOverview";
import { RationalDashboardWidgets } from "../../components/Machines/RationalDashboardWidgets";
import { ElettrobarDashboardWidgets } from "../../components/Machines/ElettrobarDashboardWidgets";
import { FrigomatDashboardWidgets } from "../../components/Machines/FrigomatDashboardWidgets";
import { IfiGalileoDashboardWidgets } from "../../components/Machines/IfiGalileoDashboardWidgets";
import { BarChartCard } from "../../components/Machines/charts/BarChartCard";
import { PieChartCard } from "../../components/Machines/charts/PieChartCard";
import { MachinesMapWidget } from "../../components/Machines/MachinesMapWidget";
import "../../styles/rational.css";

const PROVIDER_LABEL: Record<string, string> = {
  IRINOX: "Irinox",
  RATIONAL: "Rational",
  FRIGOMAT: "Frigomat",
  IFI_GALILEO: "IFI Galileo",
  ELETTROBAR: "Elettrobar",
};

export default function VendorMachinesPage() {
  const { provider = "" } = useParams();
  const { token } = useAuth() as any;

  const providerKey = String(provider).toUpperCase();
  const providerLabel = PROVIDER_LABEL[providerKey] ?? providerKey;

  const [machines, setMachines] = useState<MachineDashboardRow[]>([]);
  const [overview, setOverview] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const allowed = useMemo(
    () => ["IRINOX", "RATIONAL", "FRIGOMAT", "IFI_GALILEO", "ELETTROBAR"].includes(providerKey),
    [providerKey],
  );

  useEffect(() => {
    if (!allowed) return;
    setLoading(true);

    Promise.all([
      dashboardApi.getMachinesByProvider(token, providerKey),
      dashboardApi.getVendorOverview(token, providerKey),
    ])
      .then(([m, o]) => {
        setMachines(m as any);
        setOverview(o as any);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token, providerKey, allowed]);

  if (!allowed) {
    return (
      <div className="machines-hero">
        <div className="alert alert-warning mb-0">
          Provider non valido: <b>{provider}</b>
        </div>
        <Link to="/machines" className="btn btn-outline-secondary mt-3">
          <i className="bi bi-arrow-left me-2" />
          Torna a Macchine
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="vendor-page-header">
        <div>
          <h1 className="vendor-page-title mb-1">
            Macchine • {providerLabel}
          </h1>
          <p className="vendor-page-sub mb-0">
            KPI, consumi, allarmi e lista macchine
          </p>
        </div>
        <Link to="/machines" className="btn btn-outline-secondary">
          <i className="bi bi-arrow-left me-2" />
          Tutti i vendor
        </Link>
      </div>

      {loading && (
        <div className="machines-section-card p-4">
          <div className="text-muted d-flex align-items-center gap-2">
            <div className="spinner-border spinner-border-sm" />
            Caricamento…
          </div>
        </div>
      )}

      {!loading && overview && (
        <>
          {providerKey === "RATIONAL" && (
            <div className="rational-hub mb-4">
              <div className="rational-hub-hero">
                <div className="rational-hub-brand">
                  <div className="rational-hub-icon"><i className="bi bi-cpu-fill" /></div>
                  <div>
                    <h2 className="rational-hub-title">Rational</h2>
                    <p className="rational-hub-sub">Connected Cooking · Dettagli e analytics</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mb-4">
          <MachineKpiCards overview={overview} />
          </div>

          {/* Mappa macchine per provider */}
          <div className="mb-4">
            <MachinesMapWidget
              machines={machines
                .map((m: any) => {
                  const parseCoord = (v: unknown): number | null => {
                    if (typeof v === "number" && !Number.isNaN(v)) return v;
                    if (typeof v === "string") {
                      const n = parseFloat(v);
                      return !Number.isNaN(n) ? n : null;
                    }
                    return null;
                  };
                  const lat = parseCoord(m.latitude) ?? parseCoord(m.lat);
                  const lng = parseCoord(m.longitude) ?? parseCoord(m.lng);
                  if (lat == null || lng == null) return null;
                  return { ...m, latitude: lat, longitude: lng };
                })
                .filter(Boolean) as any}
              providerLabel={providerLabel}
            />
            {["IRINOX", "RATIONAL", "FRIGOMAT", "IFI_GALILEO"].includes(providerKey) && (
              <p className="small text-muted mt-2 mb-0">
                <i className="bi bi-geo-alt me-1" />
                Le macchine {providerLabel} si trovano nella zona <strong>Via Zanardelli</strong> – <strong>Via Cesare Abba</strong>, Lecce.
                {" "}
                <Link to="/machines" className="text-decoration-none">
                  Vedi mappa completa (tutti i provider)
                </Link>
              </p>
            )}
          </div>

          {providerKey === "IRINOX" && machines.length > 0 && (
            <>
              <div className="mb-4">
                <IrinoxEnergyOverview />
              </div>
              <div className="mb-4">
                <IrinoxConsumptionChart machines={machines} />
              </div>
            </>
          )}

          {providerKey === "RATIONAL" && (
            <div className="mb-4">
              <RationalDashboardWidgets />
            </div>
          )}

          {providerKey === "ELETTROBAR" && (
            <div className="mb-4">
              <ElettrobarDashboardWidgets />
            </div>
          )}

          {providerKey === "FRIGOMAT" && (
            <div className="mb-4">
              <FrigomatDashboardWidgets />
            </div>
          )}

          {providerKey === "IFI_GALILEO" && (
            <div className="mb-4">
              <IfiGalileoDashboardWidgets />
            </div>
          )}

          {providerKey !== "ELETTROBAR" && (
          <div className="row g-4 mb-4">
            <div className="col-12 col-lg-7">
              <BarChartCard
                title="Consumi (kWh) / ultimi 7 giorni"
                data={Array.isArray(overview.energyLast7d) ? overview.energyLast7d : []}
                xKey="day"
                barKey="kwh"
              />
            </div>
            <div className="col-12 col-lg-5">
              <PieChartCard
                title="Allarmi per macchina"
                data={Array.isArray(overview.alarmsByMachine) && overview.alarmsByMachine.length > 0
                  ? overview.alarmsByMachine
                  : (Array.isArray(overview.alarmsByCode) ? overview.alarmsByCode : [])}
                nameKey="name"
                valueKey="value"
              />
            </div>
          </div>
          )}
        </>
      )}

        <MachinesTable provider={providerKey} machines={machines} />
    </div>
  );
}
